/**
 * Sub-page 5: survivorship.
 *
 * When two records merge, every field has to be decided, and there are exactly
 * two ways that can go: keep the most recent record, or keep the value from
 * the source you trust more.
 *
 * The rules are layered. One *default* (`property_key = "*"`) decides every
 * property. A *field override* names one property and decides that property
 * instead, applied after the default. The tester shows not just which value
 * won but why, because "the CRM said so" is the part people actually need.
 */

import { useState } from 'react'
import { EmptyState, Modal } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Field, SectionHeader, Value } from '../../components'
import { useSources, useStrategies, useSurvivorshipMutations } from '../../hooks'
import { WorkedExample } from './WorkedExample'
import type {
  NodeTypeDetail,
  StrategySpec,
  SurvivorshipRule,
  SurvivorshipStrategy,
  SurvivorshipTestResult,
} from '../../types/brain.types'

/** A glyph per strategy. There are only two, and both earn one. */
const STRATEGY_ICON: Record<SurvivorshipStrategy, string> = {
  recency: '⏱',
  source_system: '↕',
}

const STRATEGY_LABEL: Record<SurvivorshipStrategy, string> = {
  recency: 'Recency',
  source_system: 'Source system',
}

/**
 * The backend normalises anything older onto these two, but a stale cached
 * response should still render a word rather than blank.
 */
const labelFor = (strategy: string) =>
  STRATEGY_LABEL[strategy as SurvivorshipStrategy] ?? strategy.replace(/_/g, ' ')

/** Returns the list with two entries exchanged, leaving the original alone. */
function swap<T>(list: T[], a: number, b: number): T[] {
  const next = [...list]
  ;[next[a], next[b]] = [next[b], next[a]]
  return next
}

export function SurvivorshipPanel({
  graphId,
  nodeTypeId,
  detail,
}: {
  graphId: string
  nodeTypeId: string
  detail: NodeTypeDetail
}) {
  const strategies = useStrategies(graphId)
  const { remove, test } = useSurvivorshipMutations(graphId, nodeTypeId)
  const toast = useToast()

  const [editing, setEditing] = useState<SurvivorshipRule | 'new' | null>(null)
  const [result, setResult] = useState<SurvivorshipTestResult | null>(null)

  const byProperty = new Map(detail.survivorship_rules.map((r) => [r.property_key, r]))
  const fallback = byProperty.get('*')
  const overrides = detail.survivorship_rules.filter((r) => r.property_key !== '*')

  const summary = (strategy: string) =>
    strategies.data?.find((s) => s.strategy === strategy)?.summary ?? ''

  /** Properties that do not already have an override, so one can be added. */
  const available = detail.properties.filter((p) => !byProperty.has(p.key))

  return (
    <div className="stack">
      <SectionHeader
        title="Survivorship"
        hint="Which value wins when two records describe the same thing. The default decides every field; a field override decides one field instead. Every decision is written to the merge audit, so a golden record can always explain itself."
      />

      {/* --- the default ---------------------------------------------------- */}

      <div className="card stack">
        <SectionHeader
          title="Default"
          hint="Applied to every property that has no override of its own."
          action={
            <button className="btn btn-sm" onClick={() => setEditing(fallback ?? 'new')}>
              {fallback ? 'Change' : 'Set the default'}
            </button>
          }
        />

        {fallback ? (
          <div className="cp-prio" style={{ gridTemplateColumns: 'auto 1fr' }}>
            <span className="n" aria-hidden="true">
              {STRATEGY_ICON[fallback.strategy] ?? '◆'}
            </span>
            <span className="nm">
              <strong>{labelFor(fallback.strategy)}</strong>
              <div className="small faint">{summary(fallback.strategy)}</div>
              {fallback.strategy === 'source_system' && (
                <div className="mono small" style={{ marginTop: 4 }}>
                  {fallback.source_priority.length
                    ? fallback.source_priority.join(' → ')
                    : 'no sources ranked yet — ties fall through to recency'}
                </div>
              )}
            </span>
          </div>
        ) : (
          <div className="callout callout-warn">
            <p>
              No default is set, so every field falls back to <strong>recency</strong> — the most
              recent record wins. That is a reasonable default, but set it explicitly so the
              behaviour is on the record rather than implied.
            </p>
          </div>
        )}
      </div>

      {/* --- field overrides ------------------------------------------------ */}

      <div className="card stack">
        <SectionHeader
          title="Field overrides"
          hint="A named property decided differently from the default. Applied after the default, and only to that field."
          action={
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setEditing('new')}
              disabled={available.length === 0}
              title={
                available.length === 0
                  ? 'Every property already has an override.'
                  : undefined
              }
            >
              Add field override
            </button>
          }
        />

        {overrides.length === 0 ? (
          <EmptyState title="No field overrides">
            Every property is decided by the default above. Add an override when one field should
            be settled differently — an owner that always comes from the CRM, say, while
            everything else takes the newest value.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Winning value</th>
                  <th>Source order</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {overrides.map((rule) => (
                  <tr key={rule.survivorship_rule_id}>
                    <td className="mono">
                      <strong>{rule.property_key}</strong>
                    </td>
                    <td>
                      <span aria-hidden="true">{STRATEGY_ICON[rule.strategy] ?? '◆'} </span>
                      {labelFor(rule.strategy)}
                      <div className="small faint">{summary(rule.strategy)}</div>
                    </td>
                    <td className="mono small">
                      {rule.strategy === 'source_system'
                        ? rule.source_priority.length
                          ? rule.source_priority.join(' → ')
                          : 'unranked'
                        : '—'}
                    </td>
                    <td>
                      <div className="row-tight">
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(rule)}>
                          Edit
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={async () => {
                            if (
                              !window.confirm(
                                `Delete the override for '${rule.property_key}'? It will fall back to the default.`,
                              )
                            )
                              return
                            try {
                              await remove.mutateAsync(rule.survivorship_rule_id)
                              toast.success('Override deleted.')
                            } catch (error) {
                              toast.error(error)
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* The live example sits above the tester: it answers the question
          everybody has before they know how to ask it. */}
      <WorkedExample detail={detail} rules={detail.survivorship_rules} />

      <MergeTester
        detail={detail}
        result={result}
        busy={test.isPending}
        onRun={async (candidates) => {
          try {
            setResult(await test.mutateAsync(candidates))
          } catch (error) {
            toast.error(error)
          }
        }}
      />

      {editing && (
        <SurvivorshipDialog
          graphId={graphId}
          nodeTypeId={nodeTypeId}
          detail={detail}
          strategies={strategies.data ?? []}
          taken={new Set(byProperty.keys())}
          existing={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function MergeTester({
  detail,
  result,
  busy,
  onRun,
}: {
  detail: NodeTypeDetail
  result: SurvivorshipTestResult | null
  busy: boolean
  onRun: (candidates: Record<string, unknown>[]) => void
}) {
  const fields = detail.properties.slice(0, 8)
  const [a, setA] = useState<Record<string, string>>({})
  const [b, setB] = useState<Record<string, string>>({})
  const [sourceA, setSourceA] = useState('crm')
  const [sourceB, setSourceB] = useState('email')

  return (
    <div className="card stack">
      <SectionHeader
        title="Try a merge"
        hint="Two versions of the same record, folded by these rules. Nothing is saved."
      />

      <div className="diffpair">
        <div className="stack" style={{ gap: 6 }}>
          <input
            className="input"
            value={sourceA}
            onChange={(e) => setSourceA(e.target.value)}
            placeholder="source id for A"
          />
          {fields.map((property) => (
            <input
              key={property.property_id}
              className="input"
              placeholder={property.key}
              value={a[property.key] ?? ''}
              onChange={(e) => setA((v) => ({ ...v, [property.key]: e.target.value }))}
            />
          ))}
        </div>
        <div className="stack" style={{ gap: 6 }}>
          <input
            className="input"
            value={sourceB}
            onChange={(e) => setSourceB(e.target.value)}
            placeholder="source id for B"
          />
          {fields.map((property) => (
            <input
              key={property.property_id}
              className="input"
              placeholder={property.key}
              value={b[property.key] ?? ''}
              onChange={(e) => setB((v) => ({ ...v, [property.key]: e.target.value }))}
            />
          ))}
        </div>
      </div>

      <button
        className="btn btn-primary"
        disabled={busy}
        onClick={() =>
          onRun([
            {
              source_id: sourceA,
              properties: Object.fromEntries(Object.entries(a).filter(([, v]) => v !== '')),
              confidence: 1,
            },
            {
              source_id: sourceB,
              properties: Object.fromEntries(Object.entries(b).filter(([, v]) => v !== '')),
              confidence: 1,
            },
          ])
        }
      >
        {busy ? 'Folding…' : 'Fold them'}
      </button>

      {result && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Winner</th>
                <th>Discarded</th>
                <th>Decided by</th>
              </tr>
            </thead>
            <tbody>
              {result.decisions.map((decision) => (
                <tr key={decision.field}>
                  <td className="mono">{decision.field}</td>
                  <td>
                    <strong>
                      <Value value={decision.winning_value} />
                    </strong>
                    {decision.source_id && (
                      <div className="small faint">from {decision.source_id}</div>
                    )}
                  </td>
                  <td className="muted">
                    {decision.losing_values.length ? (
                      decision.losing_values.map((value, i) => (
                        <div key={i}>
                          <Value value={value} />
                        </div>
                      ))
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="small muted">
                    <span className="badge badge-neutral">
                      {decision.from_field_override ? 'field override' : 'default'}
                    </span>{' '}
                    {labelFor(decision.strategy)}
                    {decision.detail ? ` — ${decision.detail}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SurvivorshipDialog({
  graphId,
  nodeTypeId,
  detail,
  strategies,
  taken,
  existing,
  onClose,
}: {
  graphId: string
  nodeTypeId: string
  detail: NodeTypeDetail
  strategies: StrategySpec[]
  taken: Set<string>
  existing?: SurvivorshipRule
  onClose: () => void
}) {
  const { create, update } = useSurvivorshipMutations(graphId, nodeTypeId)
  const toast = useToast()

  // A new rule defaults to the first property without an override, so the
  // common case — adding an override — needs no thought. The default rule is
  // reached by its own button, which passes the existing `*` rule.
  const firstFree = detail.properties.find((p) => !taken.has(p.key))?.key
  const [propertyKey, setPropertyKey] = useState(
    existing?.property_key ?? firstFree ?? '*',
  )
  const [strategy, setStrategy] = useState<SurvivorshipStrategy>(
    existing?.strategy ?? 'recency',
  )
  const [priority, setPriority] = useState<string[]>(existing?.source_priority ?? [])

  const isDefault = propertyKey === '*'

  // The ranking is over this graph's own sources, so the names are the ones
  // from the Sources tab rather than ids typed from memory.
  const sources = useSources(graphId)
  const attached = sources.data?.sources ?? []
  const label = (id: string) => attached.find((s) => s.source_id === id)?.display_name ?? id

  /** The ranked list, in order, resolved to names. */
  const ranked = priority.map((id) => ({ id, label: label(id) }))
  /** Sources this graph has that nobody has ranked yet. */
  const unranked = attached
    .filter((s) => !priority.includes(s.source_id))
    .map((s) => ({ id: s.source_id, label: s.display_name }))

  const busy = create.isPending || update.isPending

  async function save() {
    const body = {
      property_key: propertyKey,
      strategy,
      // Sending the ranking only when it is read keeps a stale list from
      // reappearing if the rule is switched back to source system later.
      source_priority: strategy === 'source_system' ? priority : [],
    }
    try {
      if (existing) await update.mutateAsync({ id: existing.survivorship_rule_id, body })
      else await create.mutateAsync(body)
      toast.success(isDefault ? 'Default saved.' : 'Field override saved.')
      onClose()
    } catch (error) {
      toast.error(error)
    }
  }

  const title = existing
    ? existing.property_key === '*'
      ? 'Default survivorship'
      : `Override for ${existing.property_key}`
    : 'New field override'

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="stack">
        {!existing && (
          <Field
            label="Field"
            required
            help="This field alone is decided by the rule below. Everything else keeps the default."
          >
            <select
              className="select"
              value={propertyKey}
              onChange={(e) => setPropertyKey(e.target.value)}
            >
              {detail.properties
                .filter((p) => !taken.has(p.key))
                .map((property) => (
                  <option key={property.property_id} value={property.key}>
                    {property.key}
                  </option>
                ))}
            </select>
          </Field>
        )}

        {/* Picking how a field is decided is the whole point of the dialog, so
            the two strategies are laid out as cards carrying their own
            explanation rather than hidden behind a select. */}
        <Field label="Winning value" required>
          <div className="cp-survgrid">
            {strategies.map((spec) => (
              <button
                key={spec.strategy}
                type="button"
                className="cp-sg"
                data-on={strategy === spec.strategy}
                aria-pressed={strategy === spec.strategy}
                onClick={() => setStrategy(spec.strategy)}
              >
                <div className="sgi" aria-hidden="true">
                  {STRATEGY_ICON[spec.strategy] ?? '◆'}
                </div>
                <div className="sgn">{spec.label}</div>
                <div className="sgd">{spec.summary}</div>
              </button>
            ))}
          </div>
        </Field>

        {strategy === 'source_system' && (
          <Field
            label="Source order, best first"
            required
            help="The first source wins; the last is the least trusted. A source that is not listed ranks below all of these, and two records from the same source are separated by recency."
          >
            {sources.isLoading && <p className="small faint">Loading this graph's sources…</p>}

            {!sources.isLoading && !ranked.length && !unranked.length && (
              <p className="small faint">
                This graph has no sources yet. Attach one on the Sources tab and it will appear
                here to rank. Until then this rule behaves as recency.
              </p>
            )}

            {ranked.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {ranked.map((source, index) => (
                  <div className="cp-prio" key={`${source.id}-${index}`}>
                    <span className="n">{index + 1}</span>
                    <span className="nm">{source.label}</span>
                    <span className="rank">
                      {index === 0
                        ? 'wins outright'
                        : index === ranked.length - 1
                          ? 'last resort'
                          : `beaten by ${index}`}
                    </span>
                    <button
                      type="button"
                      className="cp-parr"
                      aria-label={`Move ${source.label} up`}
                      disabled={index === 0}
                      onClick={() => setPriority((list) => swap(list, index, index - 1))}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="cp-parr"
                      aria-label={`Move ${source.label} down`}
                      disabled={index === ranked.length - 1}
                      onClick={() => setPriority((list) => swap(list, index, index + 1))}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="cp-parr"
                      data-danger="true"
                      aria-label={`Remove ${source.label} from the ranking`}
                      onClick={() => setPriority((list) => list.filter((_, i) => i !== index))}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Sources this graph has that are not ranked yet. They already
                rank below everything listed, so adding one is about making
                that explicit rather than changing the outcome. */}
            {unranked.length > 0 && (
              <div className="chiprow">
                {unranked.map((source) => (
                  <button
                    key={source.id}
                    type="button"
                    className="chip-filter"
                    onClick={() => setPriority((list) => [...list, source.id])}
                  >
                    + {source.label}
                  </button>
                ))}
              </div>
            )}
          </Field>
        )}
      </div>
    </Modal>
  )
}
